import get from "lodash/get";
import IdcardOutlined from "@ant-design/icons/IdcardOutlined";
import isEmpty from "lodash/isEmpty";
import { forwardRef } from "react";

import { Box } from "../box";
import { BodyHeader } from "../body-header";
import { BodyIcon } from "../body-icon";
import { BodyText } from "../body-text";
import { Spotlight } from "../../spotlight";
import { Step } from "../guide";
import { useOnClick } from "../use-on-click";
import { useOnExists } from "../use-on-exists";
import { useOnItemsCreated } from "../use-on-items-created";

const Guide = (props) => {
  const { guide, isComplete, contacts = [], patchSetupState } = props;

  const steps = [
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step onClose={guide.stop}>
            This is how you get to your contacts, click here to go there now.
          </Step>
        ),
        placement: "right",
      },
      elementId: "contacts-menu-item",
    },
    {
      tooltip: {
        content: (
          <Step onNext={guide.forward} onClose={guide.stop}>
            Here is where your contacts will show up after you create them.
          </Step>
        ),
        placement: "top",
      },
      elementId: "CONTACT-view-table",
    },
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step onBack={guide.back} onNext={guide.forward} onClose={guide.stop}>
            This is the primary way to create contacts, click to add one now, or
            continue to use the import tool.
          </Step>
        ),
        placement: "bottom",
      },
      elementId: "create-contact-button",
    },
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step
            onBack={guide.back}
            onNext={async () => {
              if (!isComplete) {
                patchSetupState.mutate({
                  isContactsStepComplete: true,
                });
              }

              guide.go(steps.length);
            }}
            nextText={"Got it"}
            onClose={guide.stop}
          >
            Click here to import your contact data if you are coming from
            another system.
          </Step>
        ),
        placement: "bottom",
      },
      elementId: "import-CONTACT-button",
    },
  ];

  useOnExists(guide.forward, guide.index === 0 ? "CONTACT-view-table" : null);

  useOnClick(
    () => guide.go(steps.length),
    guide.index === 2 ? "create-contact-button" : null
  );

  useOnClick(
    () => guide.go(steps.length),
    guide.index === 3 ? "import-CONTACT-button" : null
  );

  useOnItemsCreated(guide.complete, contacts);

  const step = steps[guide.index];

  if (!step) {
    return null;
  }

  return (
    <Spotlight
      allowClicks={step.allowClicks}
      elementId={step.elementId}
      tooltip={step.tooltip}
    />
  );
};

const HeroButton = (props, ref) => {
  const { isComplete, isCurrent, onClick } = props;

  const bodyText = (
    <>
      Contacts are who you build relationships with and want to provide value
      to.
    </>
  );

  return (
    <Box
      isComplete={isComplete}
      isCurrent={isCurrent}
      onClick={onClick}
      ref={ref}
    >
      <BodyIcon
        Icon={IdcardOutlined}
        isComplete={isComplete}
        isCurrent={isCurrent}
      />
      <BodyHeader
        isComplete={isComplete}
        isCurrent={isCurrent}
        title={"Contacts"}
      />
      <BodyText isComplete={isComplete} isCurrent={isCurrent}>
        {bodyText}
      </BodyText>
    </Box>
  );
};

const getStartIndex = () => {
  if ((window.location.href || "").endsWith("/contacts")) {
    return 1;
  }

  return 0;
};

export const contacts = {
  Guide,
  HeroButton: forwardRef(HeroButton),

  getStartIndex,
  isComplete: ({ contacts, organization }) =>
    !isEmpty(contacts) ||
    get(organization, "setupState.isContactsStepComplete"),
  mapData: ({ contacts }) => ({ contacts }),
  type: "contact",
};
